import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";
import { api, streamPost } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import logo from "@/assets/logo.png";

export default function ChatWidget() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const [sessionId] = useState(() => {
    let s = localStorage.getItem("imperiale_chat_session");
    if (!s) {
      s = crypto.randomUUID();
      localStorage.setItem("imperiale_chat_session", s);
    }
    return s;
  });

  useEffect(() => {
    if (open && messages.length === 0) {
      api
        .get(`/chat/history/${sessionId}`)
        .then((r) => setMessages(r.data))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (preset) => {
    const message = (preset ?? input).trim();
    if (!message || loading) return;
    setInput("");
    setLoading(true);
    setMessages((m) => [...m, { role: "user", text: message }, { role: "assistant", text: "" }]);
    try {
      await streamPost("/chat", { session_id: sessionId, message, lang }, (t2) => {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + t2 };
          return copy;
        });
      });
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: t.chat.error };
        return copy;
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-open-button"
        onClick={() => setOpen((o) => !o)}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 2, type: "spring", stiffness: 260, damping: 18 }}
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-bordeaux text-ivory shadow-[0_15px_40px_-10px_rgba(171,24,43,0.65)] transition-transform duration-300 hover:scale-110"
        aria-label={t.chat.openAria}
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 z-50 flex h-[70vh] max-h-[540px] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-3xl border border-ivory/15 bg-night text-ivory shadow-2xl sm:right-6"
          >
            <div className="flex items-center gap-3 border-b border-ivory/10 px-5 py-4">
              <img src={logo} alt="Imperiale" className="h-9 w-auto" />
              <div>
                <p className="font-display text-lg font-semibold">{t.chat.title}</p>
                <p className="flex items-center gap-1.5 font-mono-alt text-[9px] uppercase tracking-[0.2em] text-ivory/50">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> {t.chat.subtitle}
                </p>
              </div>
            </div>

            <div data-testid="chat-messages" className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              {messages.length === 0 && (
                <div className="space-y-3 pt-4">
                  <p className="px-2 text-center font-display text-lg italic leading-snug text-ivory/80">
                    {t.chat.welcome}
                  </p>
                  {t.chat.suggestions.map((s, i) => (
                    <button
                      key={s}
                      data-testid={`chat-suggestion-${i}`}
                      onClick={() => send(s)}
                      className="block w-full rounded-full border border-ivory/20 px-4 py-2.5 text-left text-xs text-ivory/80 transition-colors hover:bg-ivory/10"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[82%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user" ? "bg-bordeaux text-ivory" : "bg-white/10 text-ivory/90"
                    }`}
                  >
                    {m.text || <span className="animate-pulse">…</span>}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex gap-2 border-t border-ivory/10 p-3"
            >
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t.chat.placeholder}
                className="flex-1 rounded-full border border-ivory/20 bg-white/5 px-4 py-2.5 text-sm text-ivory outline-none placeholder:text-ivory/40 focus:border-ivory/50"
              />
              <button
                data-testid="chat-send-button"
                type="submit"
                disabled={loading}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-bordeaux text-ivory transition-transform hover:scale-105 disabled:opacity-50"
                aria-label={t.chat.sendAria}
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
